import { styled, withStyle } from 'baseui'
import type { Theme } from 'baseui'
import { ModalBody, ModalFooter, ModalHeader } from 'baseui/modal'
import { pulseLighting } from '@/shared/theme/pulseLighting'

interface ThemeProps {
  $theme: Theme
}

interface PulseProps extends ThemeProps {
  $pulse?: boolean
}

const radius = (value: string) => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomLeftRadius: value,
  borderBottomRightRadius: value,
})

const border = (color: string) => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const meterSearchButtonOverrides = {
  BaseButton: {
    style: {
      height: '100%',
      paddingLeft: '.5rem',
      paddingRight: '.5rem',
      letterSpacing: '-0.176px',
    },
  },
}

export const pulseMeterSearchButtonOverrides = {
  BaseButton: {
    style: {
      height: '36px',
      paddingLeft: '12px',
      paddingRight: '12px',
    },
  },
}

export const pulseModalButtonOverrides = {
  BaseButton: {
    style: {
      height: '38px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '16px',
      paddingRight: '16px',
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
    },
  },
}

export const SearchModalOverrides = {
  Dialog: {
    style: {
      width: 'auto',
      maxWidth: '90vw',
      overflow: 'hidden',
    },
  },
}

export const PulseSearchModalOverrides = {
  Dialog: {
    style: ({ $theme }: ThemeProps) => ({
      width: 'auto',
      minWidth: 'min(560px, 90vw)',
      maxWidth: '90vw',
      overflow: 'hidden',
      ...radius('12px'),
      backgroundColor: $theme.colors.white,
      boxShadow: pulseLighting($theme).shadowPulseModal,
    }),
  },
  Close: {
    style: ({ $theme }: ThemeProps) => ({
      top: '22px',
      right: '20px',
      color: $theme.colors.textPulseMuted,
    }),
  },
}

// Longhands only: against BaseUI's own longhands, a shorthand leaves the winner to Styletron's class order.
export const SearchModalHeader = withStyle(ModalHeader, ({ $theme, $pulse }: PulseProps) =>
  $pulse
    ? {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
        paddingTop: '24px',
        paddingRight: '3.5rem',
        paddingBottom: 0,
        paddingLeft: '24px',
        backgroundColor: $theme.colors.white,
        color: $theme.colors.textPulseStrong,
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 600,
      }
    : {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
        paddingTop: '1rem',
        paddingRight: '3rem',
        paddingBottom: '1rem',
        paddingLeft: '1.5rem',
        backgroundColor: $theme.colors.bgLight,
        borderBottom: `0.0625rem solid ${$theme.colors.borderFallback}`,
      },
)

export const SearchModalBody = withStyle(ModalBody, ({ $pulse }: PulseProps) =>
  $pulse
    ? { marginTop: '20px', marginBottom: 0, marginLeft: '24px', marginRight: '24px' }
    : {
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
      },
)

export const SearchModalFooter = withStyle(
  ModalFooter,
  ({ $theme, $pulse, $alignStart }: PulseProps & { $alignStart?: boolean }) =>
    $pulse
      ? {
          display: 'flex',
          justifyContent: $alignStart ? 'flex-start' : 'flex-end',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          marginRight: '24px',
          marginBottom: '24px',
          marginLeft: '24px',
          paddingTop: '16px',
          paddingRight: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          backgroundColor: $theme.colors.white,
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderTopColor: $theme.colors.borderPulse,
        }
      : {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: 0,
          paddingTop: '1rem',
          paddingRight: '1.5rem',
          paddingBottom: '1rem',
          paddingLeft: '1.5rem',
          backgroundColor: $theme.colors.bgLight,
          borderTop: `0.0625rem solid ${$theme.colors.borderFallback}`,
        },
)

export const PulseSearchFieldRow = styled<'div', { $columns: string }>('div', ({ $columns }) => ({
  display: 'grid',
  gridTemplateColumns: $columns,
  gap: '12px',
}))

export const resultsTableOverrides = {
  Root: {
    style: {
      maxHeight: '400px',
      overflow: 'auto',
    },
  },
}

export const pulseResultsTableOverrides = {
  Root: {
    style: ({ $theme }: ThemeProps) => ({
      maxHeight: '400px',
      overflow: 'auto',
      ...radius('12px'),
      ...border($theme.colors.borderPulse),
    }),
  },
  HeadCell: {
    style: ({ $theme }: ThemeProps) => ({
      backgroundColor: $theme.colors.bgPulseCanvas,
      color: $theme.colors.textPulseMuted,
      fontSize: '11.5px',
      lineHeight: '16px',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      paddingTop: '9px',
      paddingBottom: '9px',
      paddingLeft: '12px',
      paddingRight: '12px',
    }),
  },
  BodyCell: {
    style: ({ $theme }: ThemeProps) => ({
      color: $theme.colors.textPulseStrong,
      fontSize: '13.5px',
      lineHeight: '20px',
      verticalAlign: 'middle',
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '12px',
      paddingRight: '12px',
    }),
  },
}

export const PulseCounterBadge = styled<'span', { $active: boolean }>('span', ({ $theme, $active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  paddingTop: '2px',
  paddingBottom: '2px',
  paddingLeft: '9px',
  paddingRight: '9px',
  ...radius('20px'),
  fontSize: '11.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: $active ? $theme.colors.noticeOkText : $theme.colors.textPulseMuted,
  backgroundColor: $active ? $theme.colors.noticeOkBg : $theme.colors.bgPulseTrack,
}))

export const PulseResultsMessage = styled('div', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  paddingTop: '8px',
  paddingBottom: '8px',
  textAlign: 'center',
  fontSize: '13px',
  lineHeight: '19px',
  color: $theme.colors.textPulseMuted,
}))

export const PulseResultsTitle = styled('h3', ({ $theme }: ThemeProps) => ({
  margin: 0,
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 600,
  color: $theme.colors.textPulseStrong,
}))
